import { Checkbox, Field, Message, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';

import { WorksheetAssociatedTestSection, WorksheetChartSection } from 'Components/Worksheets/Core';
import { createMessageGetter } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { useSelector } from 'react-redux';
import externalTestMethods from '../../../ExternalTestMethods';

let MaterialClassificationSection = ({ createWorksheetSelector }) => {
  const importedALResultsId = useSelector(
    createWorksheetSelector('MaterialClassification.AssociatedAtterbergLimitsResults.ImportedTest.Id')
  );
  const insufficientFinesWarning = useSelector(
    pipe(
      createWorksheetSelector('MaterialClassification.Messages'),
      createMessageGetter('MaterialClassification', 'InsufficientFines'),
      (m) => m?.Text
    )
  );
  const isEstimatedAL = useSelector(
    pipe(createWorksheetSelector('MaterialClassification.AssociatedAtterbergLimitsResults.MeasuredTest'), hasValue)
  );
  const isCoarseGrained = useSelector(
    pipe(createWorksheetSelector('Preparation.CoarseOrFineGrainedMaterial'), (m) => m !== 'Fine-Grained')
  );
  const availableBorderlineGroupCodes =
    useSelector(createWorksheetSelector('Classification.AvailableBorderlineGroupCodes'), deepEquals) ?? [];
  const includeBorderlineGroupCode = useSelector(createWorksheetSelector('Classification.AppendBorderlineGroupCode'));
  const showOvenDryLL = useSelector(
    pipe(createWorksheetSelector('MaterialClassification.AlResults.LiquidLimitOvenDry'), hasValue)
  );
  const llResultNotObtainable = useSelector(
    pipe(
      createWorksheetSelector(
        'MaterialClassification.AssociatedAtterbergLimitsResults.ImportedTest.Results.LLResultCannotBeObtained'
      ),
      valueOrDefault(false)
    )
  );

  const plResultNotObtainable = useSelector(
    pipe(
      createWorksheetSelector(
        'MaterialClassification.AssociatedAtterbergLimitsResults.ImportedTest.Results.PLResultCannotBeObtained'
      ),
      valueOrDefault(false)
    )
  );
  const showALClassification = !insufficientFinesWarning;
  let showALChart = !insufficientFinesWarning && !isEstimatedAL;
  if (importedALResultsId) {
    showALChart = showALChart && !(llResultNotObtainable || plResultNotObtainable);
  }

  return (
    <>
      <ListFieldSection visible={isCoarseGrained}>
        <Field label="Main Classification" input={<TextOutput />} name="MaterialClassification.MainClassification" />
      </ListFieldSection>
      <ListFieldSection heading="Fine Material" visible={isCoarseGrained}>
        <Message info border={false} visible={!!insufficientFinesWarning}>
          {insufficientFinesWarning}
        </Message>
      </ListFieldSection>
      <WorksheetAssociatedTestSection
        label="Atterberg Limits Results"
        name="MaterialClassification.AssociatedAtterbergLimitsResults"
        testType="Atterberg Limit"
        visible={!insufficientFinesWarning}
        optionsLabels={{
          measured: 'Insufficient Material',
        }}
        defaultSelection="autoImported"
        associatedTestModel={{
          measured: true,
          autoImported: true,
          imported: {
            onlyThisSample: true,
            importedTestResults: (
              <ListFieldSection>
                <Field
                  input={<TextOutput />}
                  label="Liquid Limit"
                  name="MaterialClassification.AlResults.LiquidLimitText"
                />
                <Field
                  input={<TextOutput />}
                  label="Liquid Limit (Oven Dry)"
                  name="MaterialClassification.AlResults.LiquidLimitOvenDriedText"
                  visible={showOvenDryLL}
                />
                <Field
                  input={<TextOutput />}
                  label="Organic Material"
                  name="MaterialClassification.AlResults.OrganicMaterial"
                  format={{ type: FormatTypes.YesNo }}
                  visible={showOvenDryLL}
                />
                <Field
                  input={<TextOutput />}
                  label="Plastic Limit"
                  name="MaterialClassification.AlResults.PlasticLimitText"
                />
                <Field
                  input={<TextOutput />}
                  label="Plasticity Index"
                  name="MaterialClassification.AlResults.PlasticityIndexText"
                />
              </ListFieldSection>
            ),
            importModel: {
              Id: importedALResultsId,
            },
          },
          external: {
            testMethods: [externalTestMethods.atterbergLimit.ASTM_D4318],
            permitOtherTestMethods: true,
            externalTestResults: (
              <ListFieldSection>
                <Field
                  input={<NumericInput />}
                  label="Liquid Limit"
                  name="MaterialClassification.AssociatedAtterbergLimitsResults.ExternalTest.Results.LiquidLimit"
                />
                <Field
                  input={<NumericInput />}
                  label="Plasticity Index"
                  name="MaterialClassification.AssociatedAtterbergLimitsResults.ExternalTest.Results.PlasticityIndex"
                />
                <Field
                  input={<Checkbox />}
                  label="Material is organic"
                  name="MaterialClassification.AssociatedAtterbergLimitsResults.ExternalTest.Results.OrganicMaterial"
                />
              </ListFieldSection>
            ),
          },
        }}
      />
      <ListFieldSection visible={showALClassification}>
        <Field
          label="Material Classification"
          input={<TextOutput />}
          name="MaterialClassification.MaterialClassification"
        />
        <Field label="Material Group Code" input={<TextOutput />} name="MaterialClassification.MaterialGroupCode" />
        <FieldSet visible={!isCoarseGrained && availableBorderlineGroupCodes.length > 0}>
          <Field
            label=" Include borderline material"
            input={<Checkbox />}
            name="Classification.AppendBorderlineGroupCode"
          />
          <Field
            input={<RadioGroup />}
            name="Classification.BorderlineGroupCode"
            options={availableBorderlineGroupCodes?.map((e) => ({ label: e, value: e }))}
            visible={!!includeBorderlineGroupCode}
          />
        </FieldSet>
      </ListFieldSection>
      <NamePrefixModifier namePrefix="plasticityChart">
        <WorksheetChartSection wide height={400} visible={showALChart} />
      </NamePrefixModifier>
    </>
  );
};

MaterialClassificationSection = withInjectedProps(MaterialClassificationSection);

export { MaterialClassificationSection };
export default MaterialClassificationSection;
